import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, User as UserIcon, Anchor, BarChart3, Container as ContainerIcon, Users as UsersIcon, Crown, ScanLine, Settings as SettingsIcon, Truck, FileText } from "lucide-react";
import { PortsideStripe, PortsideMark } from "@/components/Brand";
import { useAuth } from "@/lib/auth";
import PreviewBanner from "@/components/PreviewBanner";

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const navItem = (to, label, Icon, opts = {}) => {
    const isModuleRoot = to === "/inspection" || to === "/trucking";
    const active = location.pathname === to ||
      (to === "/inspection" && (location.pathname.startsWith("/inspection") || location.pathname === "/")) ||
      (to === "/trucking" && location.pathname.startsWith("/trucking"));
    return (
      <Link
        to={isModuleRoot ? `${to}/entry` : to}
        data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
        className={`relative flex items-center gap-2 px-4 py-2 text-sm font-semibold tracking-wide uppercase transition-colors ${
          active ? "text-[#0a2952]" : "text-slate-500 hover:text-[#0a2952]"
        }`}
      >
        <Icon size={16} strokeWidth={2.2} />
        <span>{label}</span>
        {opts.primary && !active && (
          <span className="hidden lg:inline-block w-1.5 h-1.5 rounded-full bg-[#ff5b2c]" />
        )}
        {active && (
          <span className="absolute -bottom-[1px] left-3 right-3 h-[2px] bg-[#ff5b2c]" />
        )}
      </Link>
    );
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <PreviewBanner />
      <PortsideStripe />
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="max-w-[1500px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/entry" className="flex items-center gap-3" data-testid="header-logo">
            <PortsideMark />
            <span className="hidden sm:inline-block text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400 border-l border-slate-200 pl-3">
              Container Operations
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItem("/inspection", "Inspection", ScanLine, { primary: true })}
            {navItem("/trucking", "Trucking", Truck, { primary: true })}
            {navItem("/dashboard", "Dashboard", BarChart3)}
            {navItem("/waybills", "Waybills", FileText)}
            {user?.role === "admin" && navItem("/admin/users", "Users", UsersIcon)}
            {user?.role === "admin" && navItem("/admin/settings", "Settings", SettingsIcon)}
          </nav>

          <div className="relative" ref={menuRef}>
            <button
              type="button"
              data-testid="user-menu-btn"
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
            >
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover ring-2 ring-white" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#0a2952] text-white grid place-items-center text-sm font-semibold">
                  {user?.name?.[0]?.toUpperCase() || <UserIcon size={16} />}
                </div>
              )}
              <span className="hidden sm:block text-sm font-medium text-slate-700 max-w-[140px] truncate">
                {user?.name || "Account"}
              </span>
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white border border-slate-200 rounded-md shadow-lg py-1 z-50" data-testid="user-menu">
                <div className="px-4 py-3 border-b border-slate-100">
                  <div className="text-sm font-semibold text-slate-900 truncate">{user?.name}</div>
                  <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                  {user?.role === "admin" && (
                    <div className="mt-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-wider bg-[#ff5b2c]/10 text-[#ff5b2c] px-1.5 py-0.5 rounded font-bold">
                      <Crown size={10} /> Admin
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  data-testid="logout-btn"
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                >
                  <LogOut size={14} /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
        {/* Mobile nav */}
        <nav className="md:hidden flex items-center gap-1 px-4 pb-2 border-t border-slate-100 overflow-x-auto">
          {navItem("/inspection", "Inspection", ScanLine, { primary: true })}
          {navItem("/trucking", "Trucking", Truck, { primary: true })}
          {navItem("/dashboard", "Dashboard", BarChart3)}
          {navItem("/waybills", "Waybills", FileText)}
          {user?.role === "admin" && navItem("/admin/users", "Users", UsersIcon)}
          {user?.role === "admin" && navItem("/admin/settings", "Settings", SettingsIcon)}
        </nav>
      </header>

      <main className="max-w-[1500px] mx-auto px-6 py-8">{children}</main>

      <footer className="max-w-[1500px] mx-auto px-6 py-8 border-t border-slate-200 mt-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <Anchor size={14} className="text-[#0a2952]" />
            <span>Portside Container Operations · Multipurpose Terminals Ghana Ltd · Port of Tema</span>
          </div>
          <div className="font-mono-tabular">© {new Date().getFullYear()} Portside</div>
        </div>
      </footer>
    </div>
  );
}
